import { IconClock } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import type { OrderStatus } from "../../../../components/common/chip";

interface BannerCfg {
  title: string;
  bg: string;
  border: string;
  iconBg: string;
  icon: React.ReactNode;
  titleClass?: string;
}

const CFG: Partial<Record<OrderStatus, BannerCfg>> = {
  processing: {
    title: "Action required — prepare and ship this order",
    bg: "bg-accentYellowTint",
    border: "border-accentAmber",
    iconBg: "bg-accentAmberDark",
    icon: <IconClock size={24} className="text-bgWhite" />,
    titleClass: "text-accentAmberDark",
  },
};

/**
 * Formats the order-placed timestamp in the viewer's local timezone. Uses
 * Intl.DateTimeFormat so locale + timezone come from the browser, not from
 * any baked-in assumption.
 */
function formatPlacedAt(placedAt?: string | null): string {
  if (!placedAt) return "Order placed";
  const date = new Date(placedAt);
  if (Number.isNaN(date.getTime())) return "Order placed";
  const formatter = new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
  return `Placed on ${formatter.format(date)}`;
}

export function SellerOrderStatusBanner({
  status,
  placedAt,
}: {
  status: OrderStatus;
  placedAt?: string | null;
}) {
  const cfg = CFG[status];
  if (!cfg) return null;
  return (
    <div className={`flex items-center gap-4 rounded-2xl border ${cfg.border} ${cfg.bg} px-6 py-5 max-md:px-4`}>
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${cfg.iconBg}`}>
        {cfg.icon}
      </div>
      <div className="flex flex-col gap-0.5">
        <div className={cfg.titleClass}>
          <Heading variant="h3" tone={cfg.titleClass ? "inherit" : "dark"}>{cfg.title}</Heading>
        </div>
        <Text variant="body" tone={cfg.titleClass ? "inherit" : "body"}>
          {formatPlacedAt(placedAt)}
        </Text>
      </div>
    </div>
  );
}
